import {
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  notification,
  Radio,
  Select,
  Space,
  Spin,
} from 'antd'
import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import CommentList from './CommentList'
import reportError from 'domain/reportError'
import restUtils from 'domain/restUtils'

import {
  editCommentsRules,
  editDeviceCodeRules,
  editDeviceFriendlyNameRules,
  editEnrollmentDateRules,
  editHexnodeRefIdRules,
  editHologramIccidRules,
  editHologramSimIdRules,
  editImeiNumbersRules,
  editStatusRules,
  editVesselRules,
} from './deviceValidation'

const { Option } = Select
const { TextArea } = Input

const DEVICE_MAPPING_ENDPOINT = 'device'
const DeviceActionDrawer = ({
  mode,
  onClose,
  onSaveSuccess,
  record,
  requestingShipOptions,
  shipOptions,
  visible,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isSaving, setIsSaving] = useState(false)
  const [fullRecord, setFullRecord] = useState(null)
  const [isLoadingRecord, setIsLoadingRecord] = useState(false)

  const isView = mode === 'view'

  // Fetch full device details from device/{id} when the drawer opens
  useEffect(() => {
    if (!visible || !record) {
      setFullRecord(null)

      return
    }

    let cancelled = false

    const loadRecord = async () => {
      try {
        setIsLoadingRecord(true)
        const response = await restUtils.getRequest(
          `${DEVICE_MAPPING_ENDPOINT}/${record.id}`
        )
        if (!cancelled && response?.body) {
          setFullRecord(response.body)
        }
      } catch (err) {
        reportError(`DeviceActionDrawer: fetch record failed: ${err}`)
        if (!cancelled) setFullRecord(record) // fallback to basic record
      } finally {
        if (!cancelled) setIsLoadingRecord(false)
      }
    }

    loadRecord()

    return () => {
      cancelled = true
    }
  }, [visible, record])

  // Populate non-vessel form fields once the full record is loaded
  useEffect(() => {
    if (visible && fullRecord) {
      form.setFieldsValue({
        // In edit mode the comments field is intentionally left blank
        comments: '',
        deviceCode: fullRecord.deviceCode ?? '',
        deviceFriendlyName:
          fullRecord.name ?? fullRecord.deviceFriendlyName ?? '',
        enrollmentDate: fullRecord.enrolledDate
          ? dayjs(fullRecord.enrolledDate)
          : null,
        hexnodeRefId:
          fullRecord.hexnodeId != null ? String(fullRecord.hexnodeId) : '',
        hologramIccid:
          fullRecord.hologramIccid != null
            ? String(fullRecord.hologramIccid)
            : '',
        hologramSimId:
          fullRecord.hologramId != null ? String(fullRecord.hologramId) : '',
        imeiNumbers: fullRecord.imeiNumbers ?? '',
        status: fullRecord.status ? 'active' : 'inactive',
      })
    }
  }, [visible, fullRecord, form])

  // When the fullRecord loads, pre-select vessel using the provided shipOptions
  useEffect(() => {
    if (!visible || isView || !fullRecord) return

    const options = shipOptions || []

    const shipId = fullRecord.shipId
    if (shipId) {
      const match = options.find(o => o.id === shipId)
      if (match) {
        form.setFieldValue('vessel', {
          label: match.name,
          value: match.id,
        })
      }
    } else if (fullRecord.vesselName) {
      const match = options.find(o => o.name === fullRecord.vesselName)
      if (match) {
        form.setFieldValue('vessel', {
          label: match.name,
          value: match.id,
        })
      }
    }
  }, [visible, isView, fullRecord, form, shipOptions])

  const handleClose = () => {
    if (!isView) {
      form.resetFields()
    }
    setFullRecord(null)
    onClose()
  }

  const handleSave = async () => {
    try {
      await form.validateFields()
    } catch {
      // Validation errors are shown inline
      return
    }

    const values = form.getFieldsValue()

    const payload = {
      deviceCode: values.deviceCode,
      enrolledDate: values.enrollmentDate
        ? values.enrollmentDate.format('YYYY-MM-DDTHH:mm:ss')
        : null,
      hexnodeId: values.hexnodeRefId ? Number(values.hexnodeRefId) : null,
      // hologramIccid is a large telecom number — keep as string to avoid
      // JS floating-point precision loss (>15 digits).
      hologramIccid: values.hologramIccid ? String(values.hologramIccid) : null,
      hologramId: values.hologramSimId ? Number(values.hologramSimId) : null,
      // imeiNumbers is sent as a string, can contain multiple IMEIs separated by commas
      imeiNumbers: values.imeiNumbers?.trim() ?? null,
      name: values.deviceFriendlyName,
      shipId: values.vessel?.value ?? values.vessel,
      status: values.status === 'active',
    }

    // If the user entered a comments value in the edit form, include it
    // (trimmed). If the field is left blank, don't send `comments` so the
    // backend won't be asked to overwrite the existing comments array/string.
    if (values.comments && String(values.comments).trim()) {
      payload.comments = String(values.comments).trim()
    }

    try {
      setIsSaving(true)

      const response = await restUtils.putRequest(
        `${DEVICE_MAPPING_ENDPOINT}/${record.id}`,
        payload
      )
      if (!response?.ok) throw new Error(`HTTP ${response?.status}`)

      notification.success({
        description: t('deviceManagement.actionDrawer.saveSuccessDescription'),
        message: t('deviceManagement.actionDrawer.saveSuccessMessage'),
      })
      onSaveSuccess()
      handleClose()
    } catch (error) {
      reportError(`DeviceActionDrawer: PUT failed: ${error}`)
      notification.error({
        description: t('deviceManagement.actionDrawer.saveErrorDescription'),
        message: t('deviceManagement.actionDrawer.saveErrorMessage'),
      })
    } finally {
      setIsSaving(false)
    }
  }

  const drawerTitle = isView
    ? t('deviceManagement.actionDrawer.titleView')
    : t('deviceManagement.actionDrawer.titleEdit')

  const footer = isView ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Space>
        <Button onClick={handleClose}>
          {t('deviceManagement.actionDrawer.close')}
        </Button>
      </Space>
    </div>
  ) : (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Space>
        <Button disabled={isSaving} onClick={handleClose}>
          {t('deviceManagement.actionDrawer.cancel')}
        </Button>
        <Button loading={isSaving} onClick={handleSave} type="primary">
          {t('deviceManagement.actionDrawer.save')}
        </Button>
      </Space>
    </div>
  )

  if (!record) {
    return null
  }

  return (
    <Drawer
      destroyOnClose
      footer={footer}
      onClose={handleClose}
      open={visible}
      title={drawerTitle}
      width={480}
    >
      <Spin spinning={isSaving || isLoadingRecord}>
        {isView ? (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item
              label={t('deviceManagement.actionDrawer.vesselName')}
            >
              {fullRecord?.vesselName ?? record.vesselName}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('deviceManagement.registerDrawer.deviceCodeName')}
            >
              {fullRecord?.deviceCode ?? record.deviceCode}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('deviceManagement.actionDrawer.deviceFriendlyName')}
            >
              {fullRecord?.name ?? record.name}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('deviceManagement.actionDrawer.status')}
            >
              {(fullRecord?.status ?? record.status)
                ? t('deviceManagement.registerDrawer.statusActive')
                : t('deviceManagement.registerDrawer.statusInactive')}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('deviceManagement.actionDrawer.enrollmentDate')}
            >
              {fullRecord?.enrolledDate
                ? dayjs(fullRecord.enrolledDate).format('YYYY-MM-DD')
                : (record?.enrolledDate ?? '')}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('deviceManagement.registerDrawer.imeiNumbers')}
            >
              {fullRecord?.imeiNumbers ?? record.imeiNumbers}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('deviceManagement.actionDrawer.hexnodeRefId')}
            >
              {fullRecord?.hexnodeId ?? record.hexnodeId}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('deviceManagement.actionDrawer.hologramIccid')}
            >
              {fullRecord?.hologramIccid ?? record.hologramIccid}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('deviceManagement.actionDrawer.hologramSimId')}
            >
              {fullRecord?.hologramId ?? record.hologramId}
            </Descriptions.Item>
            <Descriptions.Item
              label={t('deviceManagement.actionDrawer.comments')}
            >
              <CommentList
                comments={
                  Array.isArray(fullRecord?.comments)
                    ? fullRecord.comments
                    : Array.isArray(record?.comments)
                      ? record.comments
                      : []
                }
              />
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Form form={form} layout="vertical" name="edit-device">
            <Form.Item
              label={t('deviceManagement.actionDrawer.vesselName')}
              name="vessel"
              rules={editVesselRules(t)}
            >
              <Select
                labelInValue
                loading={requestingShipOptions}
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                notFoundContent={
                  requestingShipOptions ? (
                    <Spin size="small" />
                  ) : (
                    t('deviceManagement.registerDrawer.noVesselsFound')
                  )
                }
                placeholder={t(
                  'deviceManagement.registerDrawer.vesselPlaceholder'
                )}
              >
                {(shipOptions || []).map(ship => (
                  <Option key={ship.id} value={ship.id}>
                    {ship.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={t('deviceManagement.registerDrawer.deviceCodeName')}
              name="deviceCode"
              required
              rules={editDeviceCodeRules(t)}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t('deviceManagement.actionDrawer.deviceFriendlyName')}
              name="deviceFriendlyName"
              required
              rules={editDeviceFriendlyNameRules(t)}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t('deviceManagement.actionDrawer.enrollmentDate')}
              name="enrollmentDate"
              rules={editEnrollmentDateRules(t)}
            >
              <DatePicker
                className="device-action-drawer__date-picker"
                format="DD-MM-YYYY"
              />
            </Form.Item>

            <Form.Item
              label={t('deviceManagement.actionDrawer.status')}
              name="status"
              required
              rules={editStatusRules(t)}
            >
              <Radio.Group>
                <Radio value="active">
                  {t('deviceManagement.registerDrawer.statusActive')}
                </Radio>
                <Radio value="inactive">
                  {t('deviceManagement.registerDrawer.statusInactive')}
                </Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label={t('deviceManagement.registerDrawer.imeiNumbers')}
              name="imeiNumbers"
              rules={editImeiNumbersRules(t)}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t('deviceManagement.actionDrawer.hexnodeRefId')}
              name="hexnodeRefId"
              rules={editHexnodeRefIdRules(t)}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t('deviceManagement.actionDrawer.hologramIccid')}
              name="hologramIccid"
              rules={editHologramIccidRules(t)}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t('deviceManagement.actionDrawer.hologramSimId')}
              name="hologramSimId"
              rules={editHologramSimIdRules(t)}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={t('deviceManagement.actionDrawer.comments')}
              name="comments"
              required
              rules={editCommentsRules(t)}
            >
              <TextArea rows={4} />
            </Form.Item>
            {/* Show the existing comments below the input so user can see history while editing */}
            <div style={{ marginBottom: 16 }}>
              <CommentList
                comments={
                  Array.isArray(fullRecord?.comments)
                    ? fullRecord.comments
                    : Array.isArray(record?.comments)
                      ? record.comments
                      : []
                }
              />
            </div>
          </Form>
        )}
      </Spin>
    </Drawer>
  )
}

DeviceActionDrawer.defaultProps = {
  mode: 'view',
  record: null,
  requestingShipOptions: false,
  shipOptions: [],
  visible: false,
}

DeviceActionDrawer.propTypes = {
  mode: PropTypes.oneOf(['view', 'edit']),
  onClose: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  record: PropTypes.object,
  requestingShipOptions: PropTypes.bool,
  shipOptions: PropTypes.array,
  visible: PropTypes.bool,
}

export default DeviceActionDrawer
